// src/components/SendUswd.js
import React, { useState } from "react";
import { toastSuccess } from "../utils/toast";

const NETWORK_FEE_USWD = 0.01;

const BackChevron = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center justify-center rounded-full p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
    aria-label="Back"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  </button>
);

/**
 * SendUswd
 *
 * Props:
 *   uswdWallet  — { available, balance, ... }
 *   uswdSocket  — { transferUSWD } returned from useUswdSocket
 *   onBack      — () => void
 *   onSuccess   — () => void  (triggers balance refresh in parent)
 */
export function SendUswd({ uswdWallet, uswdSocket, onBack, onSuccess }) {
  const [view,        setView]        = useState("send-input");
  const [sendAddress, setSendAddress] = useState("");
  const [sendAmount,  setSendAmount]  = useState("");
  const [sendError,   setSendError]   = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult,  setSendResult]  = useState(null);
  const [errors,      setErrors]      = useState({});

  const available = uswdWallet?.available ?? 0;

  const handleMax = () => {
    // MAX = available minus fee so the total never exceeds available
    const maxAmount = Math.max(0, Number((available - NETWORK_FEE_USWD).toFixed(2)));
    if (maxAmount > 0) {
      setSendAmount(String(maxAmount));
      setErrors((e) => ({ ...e, amount: null }));
      setSendError(null);
    }
  };

  const copyValue = async (value, label) => {
    await navigator.clipboard.writeText(String(value));
    toastSuccess(`${label} copied`);
  };

  /* ============================================================
     VIEW: send-input
  ============================================================ */
  if (view === "send-input") {
    const numeric       = Number(sendAmount);
    const hasValidInput = sendAddress.trim() && sendAmount && numeric > 0;

    const handlePreview = () => {
      const nextErrors = {};
      if (!sendAddress.trim()) nextErrors.address = "Enter a recipient USWD address";
      if (!sendAmount || numeric <= 0) nextErrors.amount = "Enter a valid amount";
      else if (numeric + NETWORK_FEE_USWD > available)
        nextErrors.amount = `Insufficient balance. You have ${Number(available).toFixed(2)} USWD (includes ${NETWORK_FEE_USWD} fee).`;

      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;
      setSendError(null);
      setView("send-confirm");
    };

    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
        <div className="border-b border-neutral-200 p-4 dark:border-neutral-700">
  <div className="flex items-center gap-3">
    <BackChevron onClick={onBack} />

    <h2 className="text-base font-semibold text-orange-400">
      Send USWD
    </h2>
  </div>
</div>

        <div className="flex flex-col gap-6 p-6">
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4
                          dark:border-neutral-700 dark:bg-neutral-800">
            <div className="text-xs text-neutral-500 dark:text-neutral-400">Available Balance</div>
            <div className="mt-1 text-xl font-bold text-green-600 dark:text-green-400">
              {Number(available).toFixed(2)}{" "}
              <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">USWD</span>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-white">
                Recipient USWD Address
              </label>
              <input
                type="text"
                placeholder="Paste USWD address…"
                value={sendAddress}
                onChange={(e) => {
                  setSendAddress(e.target.value);
                  setErrors((er) => ({ ...er, address: null }));
                }}
                className={`w-full rounded-lg border px-4 py-2.5 text-sm
                  bg-white text-neutral-900 focus:outline-none focus:ring-2
                  dark:bg-neutral-900 dark:text-white
                  ${errors.address
                    ? "border-red-400 focus:ring-red-400"
                    : "border-neutral-300 focus:ring-orange-400 dark:border-neutral-600"}`}
              />
              {errors.address && (
                <div className="mt-1 text-xs text-red-500">{errors.address}</div>
              )}
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-white">
                Amount (USWD)
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={sendAmount}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "" || /^\d+(\.\d{0,2})?$/.test(raw)) {
                      setSendAmount(raw);
                      setErrors((er) => ({ ...er, amount: null }));
                    }
                  }}
                  className={`w-full rounded-lg border px-4 py-2.5 pr-16 text-sm
                    bg-white text-neutral-900 [appearance:none] focus:outline-none focus:ring-2
                    dark:bg-neutral-900 dark:text-white
                    ${errors.amount
                      ? "border-red-400 focus:ring-red-400"
                      : "border-neutral-300 focus:ring-green-500 dark:border-neutral-600"}`}
                />
                {available > NETWORK_FEE_USWD && (
                  <button
                    type="button"
                    onClick={handleMax}
                    className="absolute right-2 top-1/2 -translate-y-1/2
                               rounded-md px-2 py-1 text-xs font-semibold
                               text-green-600 hover:bg-green-50
                               dark:text-green-400 dark:hover:bg-neutral-700"
                  >
                    MAX
                  </button>
                )}
              </div>
              {errors.amount && (
                <div className="mt-1 text-xs text-red-500">{errors.amount}</div>
              )}
            </div>

            <button
              onClick={handlePreview}
              disabled={!hasValidInput}
              className="mt-2 flex items-center justify-center rounded-lg px-4 py-2.5
                         text-sm font-semibold bg-orange-400 text-white
                         hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Preview Transfer
            </button>
          </div>

          <div className="text-xs text-neutral-500 dark:text-neutral-400">
            A network fee of {NETWORK_FEE_USWD} USWD applies to all transfers.
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================
     VIEW: send-confirm
  ============================================================ */
  if (view === "send-confirm") {
    const numeric   = Number(sendAmount);
    const total     = Number((numeric + NETWORK_FEE_USWD).toFixed(2));
    const afterSend = Number((available - total).toFixed(2));

    const handleConfirm = async () => {
      setSendError(null);
      setSendLoading(true);
      try {
        const result = await uswdSocket.transferUSWD({
          recipientAddress: sendAddress.trim(),
          amount: numeric,
        });
        setSendResult(result);
        setView("send-success");
        onSuccess?.();
      } catch (err) {
        setSendError(err?.error || "Transfer failed. Please try again.");
      } finally {
        setSendLoading(false);
      }
    };

    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
       <div className="border-b border-neutral-200 p-4 dark:border-neutral-700">
  <div className="flex items-center gap-3">
    <BackChevron
      onClick={() => {
        setSendError(null);
        setView("send-input");
      }}
    />

    <h2 className="text-base font-semibold text-orange-400">
      Confirm Transfer
    </h2>
  </div>
</div>
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
          <p className="text-sm text-neutral-500">
            Please review your transfer details before confirming.
          </p>

          <div className="rounded-xl border bg-neutral-50 dark:border-neutral-700
                          dark:bg-neutral-800 divide-y dark:divide-neutral-700">
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">To</span>
              <span className="font-mono text-xs text-right max-w-[55%] break-all">
                {sendAddress.trim()}
              </span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Amount</span>
              <strong className="text-orange-500">{numeric.toFixed(2)} USWD</strong>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Network fee</span>
              <span className="text-neutral-600 dark:text-neutral-300">
                {NETWORK_FEE_USWD.toFixed(2)} USWD
              </span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm font-semibold">
              <span className="text-neutral-700 dark:text-neutral-200">Total deducted</span>
              <span className="text-orange-500">{total.toFixed(2)} USWD</span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Available after</span>
              <span className="font-semibold">{afterSend.toFixed(2)} USWD</span>
            </div>
          </div>

          <div className="rounded-xl bg-amber-50 border border-amber-200
                          dark:bg-amber-950 dark:border-amber-800 px-4 py-3">
            <p className="text-xs text-amber-700 dark:text-amber-300">
              This action cannot be undone. Ensure the recipient address is correct.
            </p>
          </div>

          {sendError && <p className="text-xs text-red-500">{sendError}</p>}
        </div>

        <div className="border-t border-neutral-200 px-6 py-4 dark:border-neutral-700 flex gap-3">
          <button
            onClick={() => { setSendError(null); setView("send-input"); }}
            disabled={sendLoading}
            className="flex-1 rounded-lg border py-2.5 text-sm font-semibold
                       dark:border-neutral-600 hover:bg-neutral-50
                       dark:hover:bg-neutral-800 transition-colors disabled:opacity-40"
          >
            Edit
          </button>
          <button
            onClick={handleConfirm}
            disabled={sendLoading}
            className="flex-1 rounded-lg bg-orange-400 py-2.5 text-sm font-semibold
                       text-white disabled:opacity-40 hover:bg-orange-500 transition-colors"
          >
            {sendLoading ? "Sending…" : "Confirm Send"}
          </button>
        </div>
      </div>
    );
  }

  /* ============================================================
     VIEW: send-success
  ============================================================ */
  if (view === "send-success" && sendResult) {
    const data         = sendResult.data;
    const serverWallet = data?.wallet;
    const fee          = data?.fee  ?? NETWORK_FEE_USWD;
    const total        = data?.total ?? Number((Number(data?.amount) + fee).toFixed(2));

    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
        <div className="flex items-center gap-3 border-b border-neutral-200 px-6 py-4 dark:border-neutral-700">
          <h2 className="text-base font-semibold">Transfer Complete</h2>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center px-6 py-8">
          <div className="mb-5 flex h-16 w-16 items-center justify-center
                          rounded-full bg-orange-100 text-orange-500 text-3xl">
            ✓
          </div>
          <h3 className="text-xl font-bold mb-1">
            {Number(data?.amount).toFixed(2)} USWD sent
          </h3>
          <p className="text-sm text-neutral-500 mb-8">Transfer completed successfully</p>

          <div className="w-full rounded-xl border bg-neutral-50 dark:border-neutral-700
                          dark:bg-neutral-800 divide-y dark:divide-neutral-700 mb-6">
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Amount sent</span>
              <strong>{Number(data?.amount).toFixed(2)} USWD</strong>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Network fee</span>
              <span className="text-neutral-600 dark:text-neutral-300">
                {Number(fee).toFixed(2)} USWD
              </span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm font-semibold">
              <span className="text-neutral-700 dark:text-neutral-200">Total deducted</span>
              <span className="text-orange-500">{Number(total).toFixed(2)} USWD</span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">To address</span>
              <span className="font-mono text-xs text-right max-w-[55%] break-all">
                {data?.recipientAddress}
              </span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">New balance</span>
              <strong className="text-green-600">
                {Number(serverWallet?.balance).toFixed(2)} USWD
              </strong>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Available</span>
              <strong>{Number(serverWallet?.available).toFixed(2)} USWD</strong>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Transaction ID</span>
              <span
                className="font-mono text-xs text-orange-500 cursor-pointer hover:underline"
                onClick={() => copyValue(data?.ledger?.txHash, "Transaction ID")}
              >
                {data?.ledger?.txHash
                  ? `${data.ledger.txHash.slice(0, 16)}…`
                  : "—"}
              </span>
            </div>
          </div>

          <button
            onClick={onBack}
            className="w-full rounded-lg bg-orange-400 py-2.5 text-sm font-semibold text-white
                       hover:bg-orange-500"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return null;
}